import { merge, sample } from 'effector';
import { debounce } from 'patronum';
import { $userRole, signout } from '@features/common';
import { formatPayloadDate } from '@features/tickets/libs/format-payload-date';
import { Value, ObjectParts, Ticket } from '../../interfaces';
import { fxUpdateAdditionalFieldsTicket } from '../additional-fields/model';
import { addClicked } from '../page/page.model';
import { $objects } from '../ticket-objects';
import { formatAdditionalFieldsToPayload } from '../ticket/ticket.init';
import {
  $ticket,
  fxGetTicketById,
  pageUnmounted,
  entityApi,
} from '../ticket/ticket.model';
import {
  $permissions,
  fxSetTicketPriority,
  fxChangeTicketStatus,
  fxChangeAppealTicket,
  fxSetEscalation,
  fxSetDescription,
  fxSetTicketType,
  checkAppeal,
  fxCheckAppeal,
  fxCheckEscalation,
  $formattedTypes,
  $formattedPrepaymentTypes,
  fxSetTicketSla,
  fxSetTicketRate,
} from './ticket-info.model';

sample({
  clock: $objects,
  source: $ticket,
  filter: ({ object }) => Boolean((object as ObjectParts)?.id),
  fn: ({ object }) => ({
    complex_id: (object as ObjectParts).id,
  }),
  target: [fxCheckEscalation, fxCheckAppeal],
});

$permissions
  .on(fxCheckEscalation.doneData, (state, data) => ({
    ...state,
    escalation: data,
  }))
  .on(fxCheckAppeal.doneData, (state, data) => ({
    ...state,
    appeal: data,
  }))
  .reset([pageUnmounted, signout, addClicked]);

sample({
  clock: entityApi.update,
  fn: ({ id, priority }: Ticket) => ({
    ticket: {
      id,
    },
    priority: {
      name: typeof priority === 'object' ? priority.id : priority,
    },
  }),
  target: fxSetTicketPriority,
});

sample({
  clock: entityApi.update,
  source: $ticket,
  filter: ({ status: oldStatus }, { status: newStatus }) => oldStatus !== newStatus,
  fn: (_, { id, status }: Ticket) => ({
    ticket: {
      id,
    },
    status: {
      name: typeof status === 'object' ? status.id : status,
    },
  }),
  target: fxChangeTicketStatus,
});

$ticket.on(fxChangeTicketStatus.fail, (state) => {
  const newState = { ...state };

  newState.status = '';

  return newState;
});

sample({
  clock: entityApi.update,
  fn: ({ id, description }: Ticket) => ({
    ticket: { id },
    description,
  }),
  target: fxSetDescription,
});

/** Сохраняю дату sla если есть разрешение для данной роли */
sample({
  clock: entityApi.update,
  source: $userRole,
  filter: (userRole: any) => {
    const slaEditFromRole =
      userRole.internal_sections_permissions?.['tickets-section-all-restriction']?.detail
        ?.info?.updateStandardTimeLimit;

    return slaEditFromRole;
  },
  fn: (_, { id, sla }: Ticket) => ({
    ticket: { id },
    sla: formatPayloadDate(sla),
  }),
  target: fxSetTicketSla,
});

/** Сохраняю дату дополнительные поля */
sample({
  clock: debounce({
    source: merge([entityApi.update]),
    timeout: 1500,
  }),
  filter: ({ fields }: Ticket) => {
    const newVersion = localStorage.getItem('ticketsNewVersion') === 'new' ? true : false;
    const fieldslength = formatAdditionalFieldsToPayload(fields).length;

    return newVersion && fieldslength;
  },
  fn: ({ id, fields }: Ticket) => ({
    ticket: { id },
    fields: formatAdditionalFieldsToPayload(fields),
  }),
  target: fxUpdateAdditionalFieldsTicket,
});

/** Перезагружаю страницу после обновления данных (можно добавить еще эффекты) */
sample({
  clock: debounce({
    source: merge([fxSetTicketSla.doneData]),
    timeout: 400,
  }),
  source: $ticket,
  fn: ({ id }) => ({
    ticket: {
      id: id as number,
    },
  }),
  target: fxGetTicketById,
});

/** Сохраняю дату рейтинг если есть разрешение для данной роли */
sample({
  clock: entityApi.update,
  source: $userRole,
  filter: (userRole: any, { rate_value, rate_is_initially_rated }: Ticket) => {
    if (rate_is_initially_rated) return false;
    if (!rate_value) return false;

    const rateEditFromRole =
      userRole.internal_sections_permissions?.['tickets-section-all-restriction']?.detail
        ?.info?.updateTimeEstimate;

    return rateEditFromRole;
  },
  fn: (_, { id, rate_value, rate_comment }: Ticket) => ({
    rate: rate_value!,
    comment: rate_comment ?? '',
    ticket: { id },
  }),
  target: fxSetTicketRate,
});

sample({
  clock: entityApi.update,
  source: { ticket: $ticket, formattedTypes: $formattedTypes },
  filter: ({ ticket, formattedTypes }, values) => {
    const newTypes = [...values.types].filter((item) => item);
    const findedNewTypeId = newTypes[0] ? newTypes.reverse()[0]?.id : null;

    const oldTypes = [...ticket.types].filter((item) => item);
    const findedOldTypeId = oldTypes[0] ? oldTypes.reverse()[0]?.id : null;

    const paidData = findedOldTypeId
      ? formattedTypes[findedOldTypeId]?.paid || false
      : false;

    const isNewStatusAndChangedType =
      values.status === 'new' && findedOldTypeId !== findedNewTypeId;

    return isNewStatusAndChangedType && !paidData;
  },
  fn: (_, values) => {
    const findedType = [...values.types].reverse().find((item: Value) => item);

    return {
      ticket: {
        id: values.id,
      },
      types: findedType?.id ? [findedType.id] : [],
    };
  },
  target: fxSetTicketType,
});

sample({
  clock: entityApi.update,
  source: $ticket,
  filter: (ticket, values) => ticket.is_appeal != values.is_appeal,
  fn: (_, { id, is_appeal }) => ({
    ticket: {
      id,
      is_appeal: Number(Boolean(is_appeal)),
    },
  }),
  target: fxChangeAppealTicket,
});

sample({
  clock: entityApi.update,
  source: $ticket,
  filter: (ticket, values) =>
    ticket.is_escalation_enabled != values.is_escalation_enabled,
  fn: (_, { id, is_escalation_enabled }) => ({
    ticket: {
      id,
    },
    escalation: {
      enabled: Number(Boolean(is_escalation_enabled)),
    },
  }),
  target: fxSetEscalation,
});

sample({
  clock: checkAppeal,
  fn: (id) => ({ complex_id: id }),
  target: fxCheckAppeal,
});

$formattedTypes.reset([pageUnmounted, signout]);
$formattedPrepaymentTypes.reset([pageUnmounted, signout]);
